/**
 * `<ContactsTable>` — the contacts collection rendered as an Attio-style ruled
 * grid (hairline column + row dividers, quiet muted header, compact text-only
 * rows, soft status/verified pills, trailing actions menu). Purely
 * presentational; data fetching + mutations live in the route.
 *
 * Per FD § 2.1 + § 5.1. Hover-prefetch on row click is wired here per
 * NFR-Design § 4.
 */

import { TableBody, TableHeader, toast } from "@cometchat-ai/ui-dashboard";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Check, Copy } from "../icons";
import { DataGrid, DataGridCell, DataGridHead, DataGridHeaderRow, DataGridRow } from "../data-grid";
import { ListSkeleton } from "../list-skeleton";
import type { Contact } from "../../lib/api/types";
import { relativeTime } from "../../lib/relative-time";

import { ContactActionsMenu } from "./ContactActionsMenu";
import { ContactStatusBadge } from "./ContactStatusBadge";

export interface ContactsTableProps {
  contacts: Contact[];
  isLoading: boolean;
  /** Show row actions (kebab); hidden when filter=deleted. */
  showActions: boolean;
  onRowClick: (contact: Contact) => void;
  onRowHover?: (contact: Contact) => void;
  onView?: (contact: Contact) => void;
  onEdit?: (contact: Contact) => void;
  onDisable?: (contact: Contact) => void;
  onEnable?: (contact: Contact) => void;
  onDelete?: (contact: Contact) => void;
  /** Called when the bottom sentinel intersects the viewport (NFR-PERF-11). */
  onReachEnd?: () => void;
  hasMore?: boolean;
  isFetchingMore?: boolean;
}

const DASH = <span className="text-muted-foreground">—</span>;

/** Hover-reveal copy button for a contact's user id — sits beside the id and
 *  flips to a tick for a moment after copying. Stops row-click propagation. */
function CopyIdButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      aria-label={copied ? "Copied" : "Copy user ID"}
      onClick={(e) => {
        e.stopPropagation();
        void navigator.clipboard?.writeText(value);
        toast.success("Copied");
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1200);
      }}
      className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:text-foreground"
    >
      {copied ? (
        <Check variant="Bold" className="h-3.5 w-3.5 text-foreground" aria-hidden="true" />
      ) : (
        <Copy className="h-3.5 w-3.5" aria-hidden="true" />
      )}
    </button>
  );
}

export function ContactsTable({
  contacts,
  isLoading,
  showActions,
  onRowClick,
  onRowHover,
  onView,
  onEdit,
  onDisable,
  onEnable,
  onDelete,
  onReachEnd,
  hasMore = false,
  isFetchingMore = false,
}: ContactsTableProps) {
  const { t } = useTranslation("contacts");
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // IntersectionObserver-driven prefetch on the bottom sentinel — per
  // NFR-Design § 4 / NFR-PERF-11. The 200px rootMargin starts the next
  // page fetch before the user actually scrolls to the bottom.
  useEffect(() => {
    if (!onReachEnd || !hasMore) return;
    const node = sentinelRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) onReachEnd();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [onReachEnd, hasMore, contacts.length]);

  const handleHover = useCallback((c: Contact) => () => onRowHover?.(c), [onRowHover]);

  if (isLoading) {
    return (
      <ListSkeleton
        rows={8}
        itemClassName="h-10 w-full"
        className="space-y-2 p-6"
        data-testid="contacts-table-skeleton"
      />
    );
  }

  return (
    <>
      <DataGrid data-testid="contacts-table">
        <TableHeader>
          <DataGridHeaderRow>
            <DataGridHead className="w-[200px] pl-6 pr-3">{t("list.table.contact")}</DataGridHead>
            <DataGridHead className="w-[150px]">{t("list.table.userId")}</DataGridHead>
            <DataGridHead className="w-[200px]">{t("list.table.email")}</DataGridHead>
            <DataGridHead className="w-[150px]">{t("list.table.phone")}</DataGridHead>
            <DataGridHead className="w-[110px]">{t("list.table.status")}</DataGridHead>
            <DataGridHead className="w-[110px]">{t("list.table.verified")}</DataGridHead>
            <DataGridHead className="w-[120px] pl-3">{t("list.table.lastSeen")}</DataGridHead>
            <DataGridHead className="w-[72px] pr-6 text-right" divider={false}>
              {showActions ? t("list.table.actions") : ""}
            </DataGridHead>
          </DataGridHeaderRow>
        </TableHeader>
        <TableBody>
          {contacts.map((c) => (
            <DataGridRow
              key={c.id}
              className="cursor-pointer"
              onClick={() => onRowClick(c)}
              onMouseEnter={handleHover(c)}
              data-testid={`contact-row-${c.id}`}
            >
              <DataGridCell className="pl-6 pr-3">
                <span className="block truncate text-[13px] text-foreground">
                  {c.displayName ?? <span className="text-muted-foreground">{c.userId}</span>}
                </span>
              </DataGridCell>
              <DataGridCell>
                <span className="flex w-full items-center gap-1.5">
                  <span className="min-w-0 flex-1 truncate underline decoration-[color:color-mix(in_oklch,var(--color-fg)_25%,transparent)] underline-offset-2 group-hover/row:text-foreground group-hover/row:decoration-foreground">
                    {c.userId}
                  </span>
                  <CopyIdButton value={c.userId} />
                </span>
              </DataGridCell>
              <DataGridCell className="truncate">{c.email ?? DASH}</DataGridCell>
              <DataGridCell className="tabular-nums">{c.phone ?? DASH}</DataGridCell>
              <DataGridCell>
                <ContactStatusBadge contact={c} />
              </DataGridCell>
              <DataGridCell>
                {c.identityVerified ? (
                  <span
                    className="inline-flex shrink-0 items-center rounded-md px-2 py-0.5 text-xs font-medium"
                    style={{
                      color: "var(--color-accent)",
                      background: "color-mix(in oklch, var(--color-accent) 12%, transparent)",
                    }}
                    data-testid={`contact-verified-${c.id}`}
                  >
                    {t("list.table.verified")}
                  </span>
                ) : (
                  DASH
                )}
              </DataGridCell>
              <DataGridCell className="whitespace-nowrap pl-3">
                {relativeTime(c.lastSeenAt)}
              </DataGridCell>
              <DataGridCell
                className="w-[72px] pr-6 text-right"
                divider={false}
                onClick={(e) => e.stopPropagation()}
              >
                {showActions ? (
                  <ContactActionsMenu
                    contact={c}
                    onView={onView ? () => onView(c) : undefined}
                    onEdit={onEdit ? () => onEdit(c) : undefined}
                    onDisable={onDisable ? () => onDisable(c) : undefined}
                    onEnable={onEnable ? () => onEnable(c) : undefined}
                    onDelete={onDelete ? () => onDelete(c) : undefined}
                  />
                ) : null}
              </DataGridCell>
            </DataGridRow>
          ))}
        </TableBody>
      </DataGrid>
      {hasMore ? (
        <div ref={sentinelRef} className="p-4 text-center text-sm text-muted-foreground">
          {isFetchingMore ? t("list.loadingMore") : null}
        </div>
      ) : null}
    </>
  );
}
